import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'de' | 'en';

interface Translations {
  // Navbar
  nav: {
    home: string;
    work: string;
    about: string;
  };
  // Catchphrase
  catchphrase: {
    line1: string;
    line2: string;
  };
  // Services
  services: {
    aktivierung: {
      title: string;
      description: string;
    };
    eventbau: {
      title: string;
      description: string;
    };
    werbetechnik: {
      title: string;
      description: string;
    };
    posm: {
      title: string;
      description: string;
    };
  };
  // Wall of Friends
  wallOfFriends: {
    title: string;
  };
  // Instagram
  instagram: {
    title: string;
    followUs: string;
  };
  // Footer
  footer: {
    tagline: string;
    sitemap: string;
    contact: string;
    legal: string;
    impressum: string;
    datenschutz: string;
    agb: string;
  };
  // About
  about: {
    heroLine1: string;
    heroLine2: string;
    heroLine3: string;
    introLeft: string;
    introRight: string;
    teamTitle: string;
    ctaTitle: string;
    ctaSubtitle: string;
    ctaButton: string;
  };
  // Service Pages
  servicePage: {
    viewProject: string;
    underConstruction: string;
    underConstructionDesc: string;
    backToHome: string;
  };
  // Project Detail
  projectDetail: {
    notFound: string;
    backToHome: string;
    back: string;
    aboutProject: string;
    services: string;
    seeMore: string;
    viewOnInstagram: string;
    date: string;
    gallery: string;
    noImage: string;
    enlargedView: string;
  };
  // Legal pages
  legal: {
    impressumTitle: string;
    agbTitle: string;
    datenschutzTitle: string;
    companyInfo: string;
    officeLocation: string;
    owner: string;
    responsibleContent: string;
    websiteContent: string;
    websiteContentText: string;
    linksTitle: string;
    linksText: string;
    legalValidityTitle: string;
    legalValidityText: string;
    copyrightTitle: string;
    copyrightText: string;
    photography: string;
    design: string;
    webDevelopment: string;
    agbScope: string;
    agbScopeText: string;
    agbContract: string;
    agbContractText: string;
    agbServices: string;
    agbServicesText: string;
    agbAssembly: string;
    agbAssemblyText: string;
    agbDelivery: string;
    agbDeliveryText: string;
    agbWarranty: string;
    agbWarrantyText: string;
    agbCopyright: string;
    agbCopyrightText: string;
    agbDataProtection: string;
    agbDataProtectionText: string;
    agbInvoicing: string;
    agbInvoicingText: string;
    agbDeposit: string;
    agbDepositText: string;
    agbAdditionalCosts: string;
    agbWithdrawal: string;
    agbWithdrawalText: string;
    agbYouthProtection: string;
    agbYouthProtectionText: string;
    agbInsurance: string;
    agbInsuranceText: string;
    agbLiability: string;
    agbLiabilityText: string;
    agbMiscellaneous: string;
    agbMiscellaneousText: string;
    agbJurisdiction: string;
    agbJurisdictionText: string;
    privacyIntro: string;
    privacyIntroText: string;
    privacyProcessing: string;
    privacyProcessingText: string;
    privacyCookies: string;
    privacyCookiesText: string;
    privacySsl: string;
    privacySslText: string;
    privacyServerLogs: string;
    privacyServerLogsText: string;
    privacyThirdParty: string;
    privacyThirdPartyText: string;
    privacyContact: string;
    privacyContactText: string;
    privacyNewsletter: string;
    privacyNewsletterText: string;
    privacyRights: string;
    privacyRightsConfirmation: string;
    privacyRightsAccess: string;
    privacyRightsCorrection: string;
    privacyRightsDeletion: string;
    privacyRightsRestriction: string;
    privacyRightsPortability: string;
    privacyRightsObjection: string;
    privacyRightsWithdrawal: string;
    privacyEmailMarketing: string;
    privacyEmailMarketingText: string;
    privacyPaidServices: string;
    privacyPaidServicesText: string;
    privacyCopyrightTitle: string;
    privacyCopyrightText: string;
    privacyDisclaimer: string;
    privacyDisclaimerText: string;
    privacyAnalytics: string;
    privacyAnalyticsText: string;
    privacySocialMedia: string;
    privacySocialMediaText: string;
    privacyChanges: string;
    privacyChangesText: string;
    source: string;
  };
  stats: {
    founded: string;
    brands: string;
    specialists: string;
  };
  contact: {
    eyebrow: string;
    heroTitle: string;
    heroSubtitle: string;
    primaryCta: string;
    swissTrust: string;
    formTitle: string;
    formSubtitle: string;
    formName: string;
    formCompany: string;
    formEmail: string;
    formMessage: string;
    formSubmit: string;
    formMailtoNote: string;
  };
  caseStudy: {
    challenge: string;
    approach: string;
    results: string;
    metrics: string;
  };
}

const translations: Record<Language, Translations> = {
  de: {
    nav: {
      home: "Home",
      work: "Work",
      about: "About",
    },
    catchphrase: {
      line1: "Aufmerksamkeit ist kein Zufall.",
      line2: "Wir machen sie planbar.",
    },
    services: {
      aktivierung: {
        title: "Aktivierung & Promotion",
        description: "Kreative Erlebnisse, Sampling-Aktionen, Gastro- und Festivalaktivierungen. Wir sorgen dafür, dass deine Marke nicht nur gesehen, sondern erlebt wird.",
      },
      eventbau: {
        title: "Eventbau & Logistik",
        description: "Planung, Transport und Aufbau von kompletten Markenwelten. Wir übernehmen die physische Realisierung. Wenn wir bauen, dann hält es.",
      },
      posm: {
        title: "Promotional Items",
        description: "Entwicklung und Produktion von hochwertigen Werbemitteln und innovativen POS-Lösungen. Maßgeschneidert für maximale Sichtbarkeit.",
      },
      werbetechnik: {
        title: "Werbetechnik",
        description: "Ob Schaufensterbeschriftung, Fahrzeugfolierung oder Grossformatdruck – wir realisieren Werbetechnik-Projekte in Zürich und der ganzen Schweiz. Von der Konzeption bis zur Montage, alles aus einer Hand.",
      },
    },
    wallOfFriends: {
      title: "WALL OF FRIENDS",
    },
    instagram: {
      title: "Latest Social Media Posts",
      followUs: "Follow Us",
    },
    footer: {
      tagline: "Wir realisieren Markenerlebnisse. Von der Idee bis zur letzten Schraube.",
      sitemap: "Sitemap",
      contact: "Kontakt",
      legal: "Legal",
      impressum: "Impressum",
      datenschutz: "Datenschutz",
      agb: "AGB",
    },
    servicePage: {
      viewProject: "Zum Projekt",
      underConstruction: "In Arbeit",
      underConstructionDesc: "Diese Seite wird gerade für dich aufgebaut. Schau bald wieder vorbei!",
      backToHome: "Zurück zur Startseite",
    },
    about: {
      heroLine1: "WIR BRINGEN",
      heroLine2: "MARKEN DAHIN,",
      heroLine3: "WO SIE WIRKEN.",
      introLeft: "Go4it ist eine Schweizer Agentur für ganzheitliche Markenaktivierung, spezialisiert auf die Umsetzung und Logistik von erlebbaren Markenerlebnissen im öffentlichen Raum, an Events, im Handel und in der Gastronomie.",
      introRight: "Mit einem starken Netzwerk, eigener Infrastruktur und einem erfahrenen Umsetzungsteam realisieren wir Projekte effizient, flexibel und markenkonform, von der Idee bis zur letzten Schraube.",
      teamTitle: "THE TEAM",
      ctaTitle: "Lass uns gemeinsam etwas Großartiges schaffen.",
      ctaSubtitle: "Bereit, deine Vision zum Leben zu erwecken? Melde dich bei uns und lass uns dein Projekt besprechen.",
      ctaButton: "Kontakt aufnehmen",
    },
    projectDetail: {
      notFound: "Projekt nicht gefunden",
      backToHome: "Zurück zur Startseite",
      back: "Zurück",
      aboutProject: "Über das Projekt",
      services: "Leistungen",
      seeMore: "Mehr sehen",
      viewOnInstagram: "Auf Instagram ansehen",
      date: "Datum",
      gallery: "Galerie",
      noImage: "Kein Bild",
      enlargedView: "Vergrösserte Ansicht",
    },
    legal: {
      impressumTitle: "IMPRESSUM",
      agbTitle: "AGB",
      datenschutzTitle: "DATENSCHUTZERKLÄRUNG",
      companyInfo: "Firmensitz & Geschäftsräume:",
      officeLocation: "Firmensitz & Geschäftsräume:",
      owner: "Inhaber:",
      responsibleContent: "Verantwortlich für den Inhalt:",
      websiteContent: "1. Inhalt der Website",
      websiteContentText: "Die \"Go4it Agency GmbH\" übernimmt keinerlei Gewähr für die Aktualität, Korrektheit, Vollständigkeit oder Qualität der bereitgestellten Informationen. Haftungsansprüche gegen \"Go4it Agency\", welche sich auf Schäden materieller oder ideeller Art beziehen, die durch die Nutzung oder Nichtnutzung der dargebotenen Informationen bzw. durch die Nutzung fehlerhafter und unvollständiger Informationen verursacht wurden, sind grundsätzlich ausgeschlossen, sofern seitens \"Go4it Agency\" kein nachweislich vorsätzliches oder grob fahrlässiges Verschulden vorliegt.",
      linksTitle: "2. Verweise und Links",
      linksText: "In einem gesonderten Bereich stellt \"Go4it Agency GmbH\" Links zu externen Angeboten zur Verfügung. Für deren Inhalte und insbesondere für Schäden, die aus der Nutzung oder Nichtnutzung solcherart dargebotener Informationen entstehen, haftet allein der Anbieter dieser Seiten.",
      legalValidityTitle: "3. Rechtswirksamkeit dieses Haftungsausschlusses",
      legalValidityText: "Dieser Haftungsausschluss ist als Teil des Internetangebotes zu betrachten, von dem aus auf diese Seite verwiesen wurde. Sofern Teile oder einzelne Formulierungen dieses Textes der geltenden Rechtslage nicht, nicht mehr oder nicht vollständig entsprechen sollten, bleiben die übrigen Teile des Dokumentes in ihrem Inhalt und ihrer Gültigkeit davon unberührt.",
      copyrightTitle: "Urheberrecht",
      copyrightText: "Urheberrecht \"Go4it Agency\". Alle Rechte vorbehalten. Alle Bilder, Grafiken, Texte, Ton-, Video- und Animationsdateien dieser Website unterliegen dem Urheberrecht oder anderen Gesetzen zum Schutz geistigen Eigentums.",
      photography: "Fotografie:",
      design: "Gestaltung:",
      webDevelopment: "Web-Entwicklung:",
      agbScope: "1. Geltungsbereich",
      agbScopeText: "Diese allgemeinen Geschäftsbedingungen (AGBs) gelten für alle Geschäftsbeziehungen zwischen der Go4it Agency GmbH und ihren Kunden.",
      agbContract: "2. Zustandekommen des Vertrages",
      agbContractText: "Ein Vertrag kommt zustande durch ein Angebot der Go4it Agency GmbH und dessen Annahme durch den Kunden.",
      agbServices: "3. Leistungen",
      agbServicesText: "Die vertraglichen Leistungen umfassen Brand Activation, Event-Marketing, Promotions, Content Creation, Bar Catering sowie den Handel mit physischen Produkten.",
      agbAssembly: "4. Montage",
      agbAssemblyText: "Die Montage erfolgt in Abhängigkeit vom Beginn der Veranstaltung am Vortag oder am ersten Veranstaltungstag.",
      agbDelivery: "5. Lieferung von Produkten",
      agbDeliveryText: "Die Lieferbedingungen für Werbeartikel und Barelemente werden separat vereinbart.",
      agbWarranty: "6. Gewährleistung und Garantien",
      agbWarrantyText: "Für den Verkauf von Produkten gewährt die Go4it Agency GmbH Gewährleistung nach gesetzlichen Vorschriften.",
      agbCopyright: "7. Urheberrecht und Nutzungsrechte",
      agbCopyrightText: "Alle im Rahmen der Content Creation erstellten Inhalte bleiben Eigentum der Go4it Agency GmbH.",
      agbDataProtection: "8. Datenschutz",
      agbDataProtectionText: "Die Go4it Agency GmbH verpflichtet sich, alle personenbezogenen Daten gemäss den schweizerischen Datenschutzgesetzen zu behandeln.",
      agbInvoicing: "9. Rechnungsstellung und Bezahlung",
      agbInvoicingText: "Nach Durchführung des Anlasses erhält der Kunde eine Rechnung, die innerhalb von 30 Tagen zu begleichen ist.",
      agbDeposit: "10. Akontozahlung",
      agbDepositText: "Übersteigt der Auftrag den Wert von CHF 10'000, wird eine Akontozahlung von mindestens 50% fällig.",
      agbAdditionalCosts: "11. Nebenkosten",
      agbWithdrawal: "12. Rücktritt vom Vertrag",
      agbWithdrawalText: "Bei einem Rücktritt berechnet die Go4it Agency entsprechende Aufwendungen je nach Zeitpunkt.",
      agbYouthProtection: "13. Jugendschutz",
      agbYouthProtectionText: "In der Schweiz ist der Verkauf von alkoholischen Getränken an Minderjährige untersagt.",
      agbInsurance: "14. Versicherungen / Bewilligungen",
      agbInsuranceText: "Der Kunde ist für den notwendigen Versicherungsschutz verantwortlich.",
      agbLiability: "15. Haftung",
      agbLiabilityText: "Verluste und Schäden, welche nicht durch das Personal verursacht wurden, werden dem Auftraggeber in Rechnung gestellt.",
      agbMiscellaneous: "16. Verschiedenes",
      agbMiscellaneousText: "Die Go4it Agency behält sich vor, die vorliegenden AGB jederzeit anzupassen.",
      agbJurisdiction: "17. Anwendbares Recht und Gerichtsstand",
      agbJurisdictionText: "Die Vereinbarung untersteht schweizerischem Recht. Gerichtsstand ist Zürich.",
      privacyIntro: "Allgemeines / Einleitung",
      privacyIntroText: "Gestützt auf Artikel 13 der Schweizerischen Bundesverfassung und die datenschutzrechtlichen Bestimmungen des Bundes hat jede Person Anspruch auf Schutz ihrer Privatsphäre.",
      privacyProcessing: "Verarbeitung personenbezogener Daten",
      privacyProcessingText: "Personenbezogene Daten sind alle Informationen, die sich auf eine identifizierte oder identifizierbare Person beziehen.",
      privacyCookies: "Cookies",
      privacyCookiesText: "Diese Website verwendet Cookies. Dabei handelt es sich um kleine Textdateien, die auf dem Endgerät des Nutzers gespeichert werden.",
      privacySsl: "SSL/TLS-Verschlüsselung",
      privacySslText: "Diese Website verwendet aus Sicherheitsgründen eine SSL/TLS-Verschlüsselung.",
      privacyServerLogs: "Server-Log-Dateien",
      privacyServerLogsText: "Der Provider dieser Website erhebt und speichert automatisch Informationen in Server-Log-Dateien.",
      privacyThirdParty: "Dienste von Drittanbietern",
      privacyThirdPartyText: "Diese Website kann Google Maps, reCAPTCHA und YouTube nutzen.",
      privacyContact: "Kontaktformular",
      privacyContactText: "Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben bei uns gespeichert.",
      privacyNewsletter: "Newsletter",
      privacyNewsletterText: "Wenn Sie den Newsletter empfangen möchten, benötigen wir eine E-Mail-Adresse von Ihnen.",
      privacyRights: "Rechte der betroffenen Person",
      privacyRightsConfirmation: "Recht auf Bestätigung",
      privacyRightsAccess: "Auskunftsrecht",
      privacyRightsCorrection: "Recht auf Berichtigung",
      privacyRightsDeletion: "Recht auf Löschung",
      privacyRightsRestriction: "Recht auf Einschränkung der Verarbeitung",
      privacyRightsPortability: "Recht auf Datenübertragbarkeit",
      privacyRightsObjection: "Widerspruchsrecht",
      privacyRightsWithdrawal: "Recht auf Widerruf",
      privacyEmailMarketing: "Widerspruch E-Mail Marketing",
      privacyEmailMarketingText: "Der Nutzung von Kontaktdaten zur Übersendung von nicht angeforderter Werbung wird widersprochen.",
      privacyPaidServices: "Kostenpflichtige Dienste",
      privacyPaidServicesText: "Für kostenpflichtige Dienstleistungen fragen wir weitere Daten ab.",
      privacyCopyrightTitle: "Urheberrechte",
      privacyCopyrightText: "Das Urheberrecht und alle anderen Rechte an den Inhalten gehören dem Betreiber dieser Website.",
      privacyDisclaimer: "Haftungsausschluss",
      privacyDisclaimerText: "Alle Angaben auf unserer Website wurden sorgfältig geprüft.",
      privacyAnalytics: "Google Analytics",
      privacyAnalyticsText: "Diese Website benutzt Google Analytics, einen Webanalysedienst.",
      privacySocialMedia: "Social Media",
      privacySocialMediaText: "Diese Website nutzt Funktionen von Facebook, Instagram und LinkedIn.",
      privacyChanges: "Änderungen",
      privacyChangesText: "Wir können diese Datenschutzrichtlinie jederzeit ohne Ankündigung ändern.",
      source: "Quelle:",
    },
    stats: {
      founded: "Gegründet",
      brands: "Marken aktiviert",
      specialists: "Spezialisten",
    },
    contact: {
      eyebrow: "Bereit für die nächste Aktivierung?",
      heroTitle: "Lass uns dein Projekt besprechen.",
      heroSubtitle: "Kurzes Briefing, klare Antwort, ehrliche Einschätzung. Wir melden uns innerhalb eines Werktags.",
      primaryCta: "Aktivierung anfragen",
      swissTrust: "Lokal verankert. Schweizweit aktiv.",
      formTitle: "Projektanfrage",
      formSubtitle: "Erzähl uns kurz von deinem Vorhaben – wir melden uns innerhalb eines Werktags.",
      formName: "Name",
      formCompany: "Unternehmen / Marke",
      formEmail: "E-Mail",
      formMessage: "Worum geht es?",
      formSubmit: "Anfrage senden",
      formMailtoNote: "Öffnet dein E-Mail-Programm mit den eingegebenen Daten.",
    },
    caseStudy: {
      challenge: "Die Herausforderung",
      approach: "Unser Ansatz",
      results: "Das Ergebnis",
      metrics: "Kennzahlen",
    },
  },
  en: {
    nav: {
      home: "Home",
      work: "Work",
      about: "About",
    },
    catchphrase: {
      line1: "Attention is not a coincidence.",
      line2: "We make it predictable.",
    },
    services: {
      aktivierung: {
        title: "Activation & Promotion",
        description: "Creative experiences, sampling campaigns, gastro and festival activations. We ensure your brand is not just seen, but experienced.",
      },
      eventbau: {
        title: "Event Construction & Logistics",
        description: "Planning, transport and construction of complete brand worlds. We handle the physical realization. When we build, it lasts.",
      },
      posm: {
        title: "Promotional Items",
        description: "Development and production of high-quality promotional materials and innovative POS solutions. Tailored for maximum visibility.",
      },
      werbetechnik: {
        title: "Signage & Print",
        description: "From window graphics and vehicle wraps to large-format printing – we deliver signage and print solutions across Zurich and all of Switzerland. Concept to installation, all from one source.",
      },
    },
    wallOfFriends: {
      title: "WALL OF FRIENDS",
    },
    instagram: {
      title: "Latest Social Media Posts",
      followUs: "Follow Us",
    },
    footer: {
      tagline: "We create brand experiences. From idea to the last screw.",
      sitemap: "Sitemap",
      contact: "Contact",
      legal: "Legal",
      impressum: "Imprint",
      datenschutz: "Privacy Policy",
      agb: "Terms & Conditions",
    },
    servicePage: {
      viewProject: "View Project",
      underConstruction: "Under Construction",
      underConstructionDesc: "This page is currently being built. Check back soon!",
      backToHome: "Back to Home",
    },
    about: {
      heroLine1: "WE BRING",
      heroLine2: "BRANDS WHERE",
      heroLine3: "THEY MATTER.",
      introLeft: "Go4it is a Swiss agency for holistic brand activation, specialized in the implementation and logistics of experiential brand experiences in public spaces, at events, in retail and in gastronomy.",
      introRight: "With a strong network, our own infrastructure and an experienced implementation team, we realize projects efficiently, flexibly and brand-compliant, from the idea to the last screw.",
      teamTitle: "THE TEAM",
      ctaTitle: "Let's create something great together.",
      ctaSubtitle: "Ready to bring your vision to life? Get in touch and let's discuss your project.",
      ctaButton: "Get in touch",
    },
    projectDetail: {
      notFound: "Project not found",
      backToHome: "Back to homepage",
      back: "Back",
      aboutProject: "About the project",
      services: "Services",
      seeMore: "See more",
      viewOnInstagram: "View on Instagram",
      date: "Date",
      gallery: "Gallery",
      noImage: "No image",
      enlargedView: "Enlarged view",
    },
    legal: {
      impressumTitle: "IMPRINT",
      agbTitle: "TERMS & CONDITIONS",
      datenschutzTitle: "PRIVACY POLICY",
      companyInfo: "Registered Office & Business Premises:",
      officeLocation: "Registered Office & Business Premises:",
      owner: "Owner:",
      responsibleContent: "Responsible for content:",
      websiteContent: "1. Website Content",
      websiteContentText: "Go4it Agency GmbH assumes no liability for the accuracy, correctness, completeness or quality of the information provided. Liability claims against Go4it Agency relating to material or immaterial damages caused by the use or non-use of the information provided are excluded.",
      linksTitle: "2. References and Links",
      linksText: "Go4it Agency GmbH provides links to external websites in a separate section. The provider of those pages is solely liable for their content and any damages arising from their use.",
      legalValidityTitle: "3. Legal Validity of this Disclaimer",
      legalValidityText: "This disclaimer is to be regarded as part of the internet publication from which reference was made to this page. If parts of this text do not comply with the current legal situation, the remaining parts of the document remain unaffected.",
      copyrightTitle: "Copyright",
      copyrightText: "Copyright Go4it Agency. All rights reserved. All images, graphics, texts, audio, video and animation files on this website are subject to copyright or other intellectual property laws.",
      photography: "Photography:",
      design: "Design:",
      webDevelopment: "Web Development:",
      agbScope: "1. Scope",
      agbScopeText: "These general terms and conditions apply to all business relationships between Go4it Agency GmbH and its customers.",
      agbContract: "2. Conclusion of Contract",
      agbContractText: "A contract is concluded through an offer by Go4it Agency GmbH and its acceptance by the customer.",
      agbServices: "3. Services",
      agbServicesText: "Contractual services include Brand Activation, Event Marketing, Promotions, Content Creation, Bar Catering and trading in physical products.",
      agbAssembly: "4. Assembly",
      agbAssemblyText: "Assembly takes place depending on the start of the event on the previous day or on the first day of the event.",
      agbDelivery: "5. Product Delivery",
      agbDeliveryText: "Delivery conditions for promotional items and bar elements are agreed separately.",
      agbWarranty: "6. Warranty and Guarantees",
      agbWarrantyText: "For the sale of products, Go4it Agency GmbH provides warranty according to statutory provisions.",
      agbCopyright: "7. Copyright and Usage Rights",
      agbCopyrightText: "All content created within the scope of Content Creation remains the property of Go4it Agency GmbH.",
      agbDataProtection: "8. Data Protection",
      agbDataProtectionText: "Go4it Agency GmbH undertakes to treat all personal data in accordance with Swiss data protection laws.",
      agbInvoicing: "9. Invoicing and Payment",
      agbInvoicingText: "After the event, the customer receives an invoice that must be paid within 30 days.",
      agbDeposit: "10. Deposit Payment",
      agbDepositText: "If the order exceeds CHF 10,000, a deposit of at least 50% is due.",
      agbAdditionalCosts: "11. Additional Costs",
      agbWithdrawal: "12. Withdrawal from Contract",
      agbWithdrawalText: "In case of withdrawal, Go4it Agency charges corresponding expenses depending on timing.",
      agbYouthProtection: "13. Youth Protection",
      agbYouthProtectionText: "In Switzerland, the sale of alcoholic beverages to minors is prohibited.",
      agbInsurance: "14. Insurance / Permits",
      agbInsuranceText: "The customer is responsible for necessary insurance coverage.",
      agbLiability: "15. Liability",
      agbLiabilityText: "Losses and damages not caused by staff will be charged to the client.",
      agbMiscellaneous: "16. Miscellaneous",
      agbMiscellaneousText: "Go4it Agency reserves the right to adjust these terms at any time.",
      agbJurisdiction: "17. Applicable Law and Jurisdiction",
      agbJurisdictionText: "The agreement is subject to Swiss law. Place of jurisdiction is Zurich.",
      privacyIntro: "General / Introduction",
      privacyIntroText: "Based on Article 13 of the Swiss Federal Constitution and federal data protection regulations, every person has the right to protection of their privacy.",
      privacyProcessing: "Processing of Personal Data",
      privacyProcessingText: "Personal data is any information relating to an identified or identifiable person.",
      privacyCookies: "Cookies",
      privacyCookiesText: "This website uses cookies. These are small text files stored on the user's device.",
      privacySsl: "SSL/TLS Encryption",
      privacySslText: "This website uses SSL/TLS encryption for security reasons.",
      privacyServerLogs: "Server Log Files",
      privacyServerLogsText: "The provider of this website automatically collects and stores information in server log files.",
      privacyThirdParty: "Third-Party Services",
      privacyThirdPartyText: "This website may use Google Maps, reCAPTCHA and YouTube.",
      privacyContact: "Contact Form",
      privacyContactText: "If you send us inquiries via the contact form, your information will be stored with us.",
      privacyNewsletter: "Newsletter",
      privacyNewsletterText: "If you want to receive the newsletter, we need an email address from you.",
      privacyRights: "Rights of Data Subjects",
      privacyRightsConfirmation: "Right to Confirmation",
      privacyRightsAccess: "Right of Access",
      privacyRightsCorrection: "Right to Rectification",
      privacyRightsDeletion: "Right to Erasure",
      privacyRightsRestriction: "Right to Restriction of Processing",
      privacyRightsPortability: "Right to Data Portability",
      privacyRightsObjection: "Right to Object",
      privacyRightsWithdrawal: "Right to Withdraw Consent",
      privacyEmailMarketing: "Objection to Email Marketing",
      privacyEmailMarketingText: "The use of contact data for sending unsolicited advertising is objected to.",
      privacyPaidServices: "Paid Services",
      privacyPaidServicesText: "For paid services, we collect additional data.",
      privacyCopyrightTitle: "Copyrights",
      privacyCopyrightText: "The copyright and all other rights to the content belong to the operator of this website.",
      privacyDisclaimer: "Disclaimer",
      privacyDisclaimerText: "All information on our website has been carefully checked.",
      privacyAnalytics: "Google Analytics",
      privacyAnalyticsText: "This website uses Google Analytics, a web analytics service.",
      privacySocialMedia: "Social Media",
      privacySocialMediaText: "This website uses features from Facebook, Instagram and LinkedIn.",
      privacyChanges: "Changes",
      privacyChangesText: "We may change this privacy policy at any time without notice.",
      source: "Source:",
    },
    stats: {
      founded: "Founded",
      brands: "Brands activated",
      specialists: "Specialists",
    },
    contact: {
      eyebrow: "Ready for your next activation?",
      heroTitle: "Let's talk about your project.",
      heroSubtitle: "Quick brief, clear answer, honest perspective. We get back to you within one business day.",
      primaryCta: "Request activation",
      swissTrust: "Swiss-based. Active across Switzerland.",
      formTitle: "Project inquiry",
      formSubtitle: "Tell us briefly about your plans — we'll respond within one business day.",
      formName: "Name",
      formCompany: "Company / Brand",
      formEmail: "Email",
      formMessage: "What's it about?",
      formSubmit: "Send inquiry",
      formMailtoNote: "Opens your email client with your data prefilled.",
    },
    caseStudy: {
      challenge: "The challenge",
      approach: "Our approach",
      results: "The outcome",
      metrics: "Key metrics",
    },
  },
};

interface LanguageContextType {
  language: Language;
  t: Translations;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('de');

  const value = {
    language,
    t: translations[language],
    setLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Fallback to prevent a blank screen if the provider is temporarily missing
    // (e.g. during hot reload). App is still expected to mount LanguageProvider.
    return {
      language: 'de' as const,
      t: translations.de,
setLanguage: (_lang: Language) => {},
    };
  }
  return context;
};
